import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  DatePicker,
  Select,
  SelectItem,
  SelectPopup,
  SelectSeparator,
  Text,
} from "@salestrak/ui";
import {
  DATE_PRESETS,
  DEFAULT_PRESET,
  parseISODate,
  type DatePresetCustom,
  type DatePresetKey,
  type DatePresetOption,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
import type { Omittable } from "@salestrak/ui";

type DateRangePresetPickerProps = Omittable<{
  value: DatePresetValue | null;
  onChange: (next: DatePresetValue) => void;
  /** The trigger — typically a `SelectTrigger`, or one wrapping a custom element via `render`. */
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  /** Optional curated subset with surface-specific labels. */
  presets?: readonly DatePresetOption[];
}>;

/**
 * Preset Select with inline custom-range pickers. Non-custom presets commit
 * immediately and close. Picking "Custom" keeps the dropdown open and reveals
 * two DatePickers; the custom range commits on close (outside click / Escape)
 * only if both dates are set.
 */
export function DateRangePresetPicker({
  value,
  onChange,
  children,
  align = "end",
  presets = DATE_PRESETS,
}: DateRangePresetPickerProps) {
  const [open, setOpen] = useState(false);
  const [draftKey, setDraftKey] = useState<DatePresetKey>(
    value?.key ?? DEFAULT_PRESET,
  );
  const [draftCustom, setDraftCustom] = useState<DatePresetCustom | null>(
    value?.custom ?? null,
  );

  const draftKeyRef = useRef(draftKey);
  const draftCustomRef = useRef(draftCustom);
  const justSelectedCustomRef = useRef(false);
  useEffect(() => {
    draftKeyRef.current = draftKey;
  }, [draftKey]);
  useEffect(() => {
    draftCustomRef.current = draftCustom;
  }, [draftCustom]);

  // Base UI's Select is value-based; `items` is what lets a SelectValue in the
  // caller's trigger render the preset LABEL rather than the raw key.
  const presetItems = useMemo(
    () => presets.map((p) => ({ value: p.key, label: p.label })),
    [presets],
  );

  const handleValueChange = useCallback(
    (next: DatePresetKey | null) => {
      if (!next) return;
      if (next === "custom") {
        justSelectedCustomRef.current = true;
        draftKeyRef.current = "custom";
        setDraftKey("custom");
        return;
      }
      draftKeyRef.current = next;
      setDraftKey(next);
      onChange({ key: next });
    },
    [onChange],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && justSelectedCustomRef.current) {
        justSelectedCustomRef.current = false;
        return;
      }
      if (next) {
        const k = value?.key ?? DEFAULT_PRESET;
        draftKeyRef.current = k;
        setDraftKey(k);
        draftCustomRef.current = value?.custom ?? null;
        setDraftCustom(value?.custom ?? null);
      } else if (draftKeyRef.current === "custom") {
        const c = draftCustomRef.current;
        if (c?.startDate && c?.endDate) {
          onChange({ key: "custom", custom: c });
        }
      }
      setOpen(next);
    },
    [value, onChange],
  );

  const handleStartChange = useCallback((date: Date | null) => {
    setDraftCustom((prev) => ({
      startDate: date ? date.toISOString() : "",
      endDate: prev?.endDate ?? "",
    }));
  }, []);

  const handleEndChange = useCallback((date: Date | null) => {
    setDraftCustom((prev) => ({
      startDate: prev?.startDate ?? "",
      endDate: date ? date.toISOString() : "",
    }));
  }, []);

  const startMatcher = useMemo(() => {
    const today = atMidnight(new Date());
    const end = parseISODate(draftCustom?.endDate);
    const endDay = end ? atMidnight(end) : null;
    return (d: Date) => {
      const day = atMidnight(d).getTime();
      if (day > today.getTime()) return true;
      if (endDay && day > endDay.getTime()) return true;
      return false;
    };
  }, [draftCustom?.endDate]);

  const endMatcher = useMemo(() => {
    const today = atMidnight(new Date());
    const start = parseISODate(draftCustom?.startDate);
    const startDay = start ? atMidnight(start) : null;
    return (d: Date) => {
      const day = atMidnight(d).getTime();
      if (day > today.getTime()) return true;
      if (startDay && day < startDay.getTime()) return true;
      return false;
    };
  }, [draftCustom?.startDate]);

  return (
    <Select
      items={presetItems}
      value={draftKey}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={handleOpenChange}
    >
      {children}
      {/* Anchored like a normal popover rather than pinned to the selected
          item: `alignItemWithTrigger` makes Base UI overlay the current value
          on the trigger and IGNORE side/align, so a picker low in a scrolling
          panel — the filter panel's last section — grows off the bottom of the
          viewport behind a scroll arrow instead of flipping above the trigger.
          It also cannot absorb the custom-range reveal, which changes the
          popup's height while it is open. Off, the positioner flips to
          whichever side has room and honours `align`. */}
      <SelectPopup align={align} alignItemWithTrigger={false}>
        {presets.map((preset) => (
          <SelectItem
            key={preset.key}
            value={preset.key}
            description={preset.description}
          >
            <Text>{preset.label}</Text>
          </SelectItem>
        ))}
        {draftKey === "custom" && (
          <>
            <SelectSeparator />
            <Box gap="sm" padding="sm">
              <DatePicker
                value={parseISODate(draftCustom?.startDate)}
                onValueChange={handleStartChange}
                placeholder="Start..."
                size="sm"
                disabledDates={startMatcher}
              />
              <DatePicker
                value={parseISODate(draftCustom?.endDate)}
                onValueChange={handleEndChange}
                placeholder="End..."
                size="sm"
                disabledDates={endMatcher}
              />
            </Box>
          </>
        )}
      </SelectPopup>
    </Select>
  );
}

function atMidnight(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
